import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button } from '@ooh/ui/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@ooh/ui/components/popover';
import type { CellEntry } from '@ooh/contracts';
import { STATUS_PRECEDENCE } from '@ooh/contracts';
import { ENTRY_STATUS_LABEL_KEYS } from './availability-cell-styles';

// Build an O(1) lookup index from the STATUS_PRECEDENCE array.
// Higher index → more dominant.
const STATUS_PRECEDENCE_INDEX: ReadonlyMap<string, number> = new Map(
  STATUS_PRECEDENCE.map((s, i) => [s, i]),
);

export function getStatusPrecedence(status: string): number {
  return STATUS_PRECEDENCE_INDEX.get(status) ?? -1;
}

/** Sort entries highest-precedence-first (SOLD first, FREE last). */
function sortEntriesByPrecedence(entries: CellEntry[]): CellEntry[] {
  return [...entries].sort((a, b) => getStatusPrecedence(b.status) - getStatusPrecedence(a.status));
}

// ─── MultiStackCellContent ────────────────────────────────────────────────────

/**
 * Renders multi-stack cell content per the canonical entries[] model.
 *
 * - Shows up to two stacked entries with "<clientName> (<holderName>)" labels.
 * - Status precedence (SOLD > HARD_RESERVATION > …) drives dominant entry ordering.
 * - "+N" indicator expands on click into a popover listing all entries.
 * - HARD_RESERVATION with conditional=true renders label text in red.
 * - Day-granularity (colWidth === 40) degrades: no stacked text labels, only +N.
 */
export interface MultiStackCellContentProps {
  entries: CellEntry[];
  /** Column pixel width — 40 = day granularity (degrade mode). */
  colWidth?: number;
}

export function MultiStackCellContent({ entries, colWidth = 80 }: MultiStackCellContentProps) {
  const { t } = useTranslation('inventory');
  const { t: tCommon } = useTranslation('common');
  const [moreOpen, setMoreOpen] = React.useState(false);

  if (entries.length === 0) return null;

  const sorted = sortEntriesByPrecedence(entries);
  const isDayGranularity = colWidth <= 40;

  // Day-granularity: show 0 labels; "+N" counts all entries except the dominant.
  // Normal: show up to 2 labels; "+N" counts entries beyond the 2nd.
  const visibleEntries = isDayGranularity ? [] : sorted.slice(0, 2);
  const overflowCount = isDayGranularity ? sorted.length - 1 : sorted.length - 2;
  const hasOverflow = overflowCount > 0;

  return (
    <div className="flex flex-col w-full h-full overflow-hidden justify-center items-start px-0.5 gap-px">
      {!isDayGranularity
        ? visibleEntries.map((entry, index) => (
            <EntryLabel
              key={entry.conflictingOfferId ?? index}
              entry={entry}
              index={index}
              tCommon={tCommon}
            />
          ))
        : null}

      {hasOverflow ? (
        <Popover open={moreOpen} onOpenChange={setMoreOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="inline"
              data-testid="more-entries-btn"
              type="button"
              aria-label={t('availability.expandStackAriaLabel')}
              onClick={(e) => {
                e.stopPropagation();
                setMoreOpen((prev) => !prev);
              }}
              className="text-2xs font-semibold text-muted-foreground leading-none px-0.5 hover:text-foreground transition-colors h-auto p-0"
            >
              {t('availability.moreEntries', { n: overflowCount })}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            data-testid="more-entries-popover"
            className="p-2 space-y-1 text-xs"
            align="start"
            onClick={(e) => e.stopPropagation()}
          >
            {sorted.map((entry, index) => (
              <EntryLabelFull
                key={entry.conflictingOfferId ?? index}
                entry={entry}
                tCommon={tCommon}
              />
            ))}
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}

function EntryLabel({
  entry,
  index,
  tCommon,
}: {
  entry: CellEntry;
  index: number;
  tCommon: TFunction<'common'>;
}) {
  const isConditional = entry.conditional === true;
  const text = buildEntryShortLabel(entry, tCommon);

  return (
    <div
      data-testid={`stacked-cell-label-${index}`}
      className="truncate text-2xs leading-tight w-full"
    >
      {isConditional ? (
        // `text-status-sold` is the same DS token InlineOwnerLabel/TooltipOwnerHint use for
        // this conditional-sale semantic — bright enough to stay legible on both the light
        // soft-reservation fill and the dark hard-reservation fill, so no dark-fill variant
        // is needed here (unlike non-conditional labels, which inherit the ancestor td's
        // own text color).
        <span data-conditional="true" className="text-status-sold">
          {text}
        </span>
      ) : (
        <span>{text}</span>
      )}
    </div>
  );
}

function EntryLabelFull({ entry, tCommon }: { entry: CellEntry; tCommon: TFunction<'common'> }) {
  const isConditional = entry.conditional === true;
  const text = buildEntryFullLabel(entry, tCommon);

  return (
    <div className="text-xs leading-snug">
      {isConditional ? (
        <span data-conditional="true" className="text-red-600">
          {text}
        </span>
      ) : (
        <span>{text}</span>
      )}
    </div>
  );
}

function buildEntryShortLabel(entry: CellEntry, tCommon: TFunction<'common'>): string {
  const parts: string[] = [];
  if (entry.clientName) parts.push(entry.clientName);
  if (entry.holderName) parts.push(`(${entry.holderName})`);
  return parts.join(' ') || tCommon(ENTRY_STATUS_LABEL_KEYS[entry.status]);
}

function buildEntryFullLabel(entry: CellEntry, tCommon: TFunction<'common'>): string {
  const parts: string[] = [tCommon(ENTRY_STATUS_LABEL_KEYS[entry.status])];
  if (entry.clientName) parts.push(entry.clientName);
  if (entry.holderName) parts.push(`(${entry.holderName})`);
  return parts.join(' ');
}
